import { RouterOutlet } from '@angular/router';
import { ConvertHelper, DataUrl, MimeTypeEnum } from '@barthauer/abc/core';
import { ISelfDescribable } from '../viewModels/mobileData.viewModel';
import { MobileDocumentViewModel } from '../viewModels/mobileData.common.viewModel';
import { Subject } from 'rxjs';

export class ViewModelHelper {
  //https://medium.com/@jakubsynowiec/unique-array-values-in-javascript-7c932682766c
  public static flattenAndDistinctToArray(data: Array<ISelfDescribable>): string[] {
    const result: string[] = [];

    const seen: Set<string> = new Set<string>();
    for (let i = 0; i < data.length; i++) {
      //const objValues = Object.values(data[i]);
      const objValues = data[i].getValues();

      for (let a = 0; a < objValues.length; a++) {
        const prop = objValues[a];
        if (!seen.has(prop)) {
          seen.add(prop);
          result.push(prop);
        }
      }
    }
    return result;
  }
  public static groupArray<T>(source: T[], propName: string): Map<string, T[]> {
    const groupedObjects: Map<string, T[]> = new Map<string, T[]>();
    for (const sourceObject of source) {
      const sourceKey = sourceObject[propName]?.toLowerCase();
      if (!groupedObjects.has(sourceKey)) {
        groupedObjects.set(sourceKey, new Array<T>());
      }
      groupedObjects.get(sourceKey).push(sourceObject);
    }
    return groupedObjects;
  }

  public static isMobileDevice() {
    let check = false;
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4),
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor);
    return check;
  }

  public static getAnimationData(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  public static ensureValidIsoString(datePart: string, newTimePart: Date): string {
    let ret = null;
    const datePartIndex = datePart?.indexOf('T');
    if (datePartIndex > 0) {
      ret = datePart.slice(0, datePartIndex) + 'T' + ConvertHelper.toISOTimeString(newTimePart);
    } else {
      ret = ConvertHelper.toISOTimeString(newTimePart);
    }
    return ret;
  }

  public static parseDateTime(datePart: Date, timeString?: string): Date {
    let retVal: Date;
    if (!datePart) {
      datePart = new Date();
    }
    if (timeString) {
      retVal = new Date(timeString);
      if (ViewModelHelper.isValidDate(retVal)) {
        retVal = new Date(datePart.toDateString() + ' ' + ConvertHelper.toISOTimeString(retVal));
      } else {
        retVal = new Date(ConvertHelper.toISODateString(datePart) + 'T' + timeString);
      }
    } else {
      retVal = new Date(datePart.toDateString());
    }
    return retVal;
  }

  public static isValidDate(d: any): boolean {
    return d instanceof Date && !isNaN(d.getTime());
  }

  public static areDatesEqualIgnoreTime(first: Date, second: Date): boolean {
    if (first && second) {
      first.setHours(0, 0, 0, 0);
      second.setHours(0, 0, 0, 0);
      return first.valueOf() === second.valueOf();
    }
    return false;
  }

  public static firstOrDefault<T>(arr: T[], predicate: (element: T) => boolean): T | undefined {
    const filteredArray = arr.filter(predicate);
    return filteredArray.length > 0 ? filteredArray[0] : undefined;
  }

  public static HOUR_IN_MILLISECONDS: number = 3600000;
  public static DAY_IN_HOURS: number = 24.0;

  public static isValidNumber(num: number): boolean {
    return num !== null && num != undefined;
  }
}

export class MTHelper {
  public static sortMobileDocuments(documents: MobileDocumentViewModel[]) {
    documents.sort((a, b) => {
      if (a.insertAt > b.insertAt) return 1;
      if (a.insertAt < b.insertAt) return -1;
      return 0;
    });
  }

  public static getUniqueDocumentName(extension: string, counter: number): string {
    const currentDate = new Date();
    return (
      'BaSYS_opera_' +
      currentDate.getFullYear() +
      ('0' + (currentDate.getMonth() + 1)).slice(-2) +
      ('0' + currentDate.getDate()).slice(-2) +
      ('0' + currentDate.getHours()).slice(-2) +
      ('0' + currentDate.getMinutes()).slice(-2) +
      ('0' + currentDate.getSeconds()).slice(-2) +
      ('0' + counter).slice(-2) +
      '.' +
      extension
    );
  }

  public static async resizeImage(dataUrl: DataUrl): Promise<DataUrl> {
    const canvas = document.createElement('canvas');
    const subject = new Subject<DataUrl>();
    const img = document.createElement('img');
    img.onload = () => {
      let height = 200;
      let width = height;
      if (img.height > 0) {
        width = (height * img.width) / img.height;
      }
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      subject.next(DataUrl.fromString(canvas.toDataURL(dataUrl.mimeType ?? MimeTypeEnum.PNG)));
      subject.complete();
    };
    img.onerror = () => {
      subject.next(null);
      subject.complete();
    };
    img.src = dataUrl.toString();

    return await subject.asObservable().toPromise();
  }
}

export class FilterButton {
  id: string;
  icon: string;
  hint: string;
  predicate: (obj: any) => boolean;
  badge: number | null;
}
